import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useBillApprovals } from "@/hooks/useApprovalPolicies";
import { CheckCircle, XCircle, Clock, DollarSign } from "lucide-react";
import { BillApprovalDialog } from "./BillApprovalDialog";

export function ApprovalDashboard({ userId }: { userId: string }) {
  const { data: approvals, isLoading } = useBillApprovals(undefined, userId);
  const [selectedBill, setSelectedBill] = useState<any>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "approved":
        return "bg-green-500/10 text-green-500 border-green-500";
      case "rejected":
        return "bg-red-500/10 text-red-500 border-red-500";
      case "pending":
        return "bg-yellow-500/10 text-yellow-500 border-yellow-500";
      default:
        return "bg-gray-500/10 text-gray-500 border-gray-500";
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-4 w-4" />;
      case "rejected":
        return <XCircle className="h-4 w-4" />;
      default:
        return <Clock className="h-4 w-4" />;
    }
  };

  if (isLoading) {
    return <div>Loading approvals...</div>;
  }

  return (
    <>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Pending Approvals</h3>
          <Badge variant="outline">{approvals?.length || 0} items</Badge>
        </div>

        <div className="space-y-3">
          {approvals?.map((approval) => (
            <Card key={approval.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-3">
                      <DollarSign className="h-5 w-5 text-muted-foreground" />
                      <p className="font-medium">Bill ID: {approval.bill_id}</p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <Badge className={getStatusColor(approval.status)}>
                        {getStatusIcon(approval.status)}
                        <span className="ml-1">{approval.status}</span>
                      </Badge>
                      {approval.approval_level && (
                        <Badge variant="outline">Level {approval.approval_level}</Badge>
                      )}
                    </div>
                  </div>

                  {approval.status === "pending" && (
                    <Button onClick={() => setSelectedBill(approval)}>Review</Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {(!approvals || approvals.length === 0) && (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                No pending approvals
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {selectedBill && (
        <BillApprovalDialog
          open={!!selectedBill}
          onOpenChange={(open) => !open && setSelectedBill(null)}
          approval={selectedBill}
        />
      )}
    </>
  );
}
